/**
 * Variants Service
 * Handles all variant-related API calls
 */

import network from '../../Network/NetworkManager';

const APPURL = require('../../Constants/URLConstants');

/**
 * Get all variant types
 * @returns {Promise<object>} - Response with variant types
 */
export const getVariantTypes = async () => {
  try {
    const response = await network.networkCall(
      APPURL.URLPaths.addVariant,
      'GET',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error fetching variant types:', error);
    throw error;
  }
};

/**
 * Get single variant type by ID
 * @param {number} variantTypeId - Variant type ID
 * @returns {Promise<object>} - Response with variant type data
 */
export const getVariantTypeById = async (variantTypeId) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.addVariant}/${variantTypeId}`,
      'GET',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error fetching variant type:', error);
    throw error;
  }
};

/**
 * Create a new variant type
 * @param {object} variantTypeData - Variant type data
 * @returns {Promise<object>} - Response with created variant type
 */
export const createVariantType = async (variantTypeData) => {
  try {
    const response = await network.networkCall(
      APPURL.URLPaths.addVariant,
      'POST',
      JSON.stringify({ variant_type: variantTypeData }),
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error creating variant type:', error);
    throw error;
  }
};

/**
 * Update a variant type
 * @param {number} variantTypeId - Variant type ID
 * @param {object} variantTypeData - Updated variant type data
 * @returns {Promise<object>} - Response with updated variant type
 */
export const updateVariantType = async (variantTypeId, variantTypeData) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.addVariant}/${variantTypeId}`,
      'PUT',
      JSON.stringify({ variant_type: variantTypeData }),
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error updating variant type:', error);
    throw error;
  }
};

/**
 * Delete a variant type
 * @param {number} variantTypeId - Variant type ID
 * @returns {Promise<object>} - Response
 */
export const deleteVariantType = async (variantTypeId) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.addVariant}/${variantTypeId}`,
      'DELETE',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error deleting variant type:', error);
    throw error;
  }
};

/**
 * Add variant type value
 * @param {number} variantTypeId - Variant type ID
 * @param {object} valueData - Value data
 * @returns {Promise<object>} - Response with created value
 */
export const addVariantTypeValue = async (variantTypeId, valueData) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.addVariant}/${variantTypeId}/values`,
      'POST',
      JSON.stringify({ variant_type_value: valueData }),
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error adding variant type value:', error);
    throw error;
  }
};

/**
 * Update variant type value
 * @param {number} variantTypeId - Variant type ID
 * @param {number} valueId - Value ID
 * @param {object} valueData - Updated value data
 * @returns {Promise<object>} - Response with updated value
 */
export const updateVariantTypeValue = async (variantTypeId, valueId, valueData) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.addVariant}/${variantTypeId}/values/${valueId}`,
      'PUT',
      JSON.stringify({ variant_type_value: valueData }),
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error updating variant type value:', error);
    throw error;
  }
};

/**
 * Delete variant type value
 * @param {number} variantTypeId - Variant type ID
 * @param {number} valueId - Value ID
 * @returns {Promise<object>} - Response
 */
export const deleteVariantTypeValue = async (variantTypeId, valueId) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.addVariant}/${variantTypeId}/values/${valueId}`,
      'DELETE',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error deleting variant type value:', error);
    throw error;
  }
};

/**
 * Create listing variant
 * @param {number} listingId - Listing ID
 * @param {object} variantData - Variant data
 * @returns {Promise<object>} - Response with created variant
 */
export const createListingVariant = async (listingId, variantData) => {
  try {
    const response = await network.networkCall(
      `v1/listings/${listingId}/variants`,
      'POST',
      JSON.stringify({ variant: variantData }),
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error creating listing variant:', error);
    throw error;
  }
};

/**
 * Create listing variants in bulk
 * @param {number} listingId - Listing ID
 * @param {array} variantsData - Array of variant data
 * @returns {Promise<object>} - Response with created variants
 */
export const createListingVariantsBulk = async (listingId, variantsData) => {
  try {
    const response = await network.networkCall(
      `v1/listings/${listingId}/variants/bulk`,
      'POST',
      JSON.stringify({ variants: variantsData }),
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error creating bulk variants:', error);
    throw error;
  }
};

/**
 * Update listing variant
 * @param {number} listingId - Listing ID
 * @param {number} variantId - Variant ID
 * @param {object} variantData - Updated variant data
 * @returns {Promise<object>} - Response with updated variant
 */
export const updateListingVariant = async (listingId, variantId, variantData) => {
  try {
    const response = await network.networkCall(
      `v1/listings/${listingId}/variants/${variantId}`,
      'PUT',
      JSON.stringify({ variant: variantData }),
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error updating listing variant:', error);
    throw error;
  }
};

/**
 * Delete listing variant
 * @param {number} listingId - Listing ID
 * @param {number} variantId - Variant ID
 * @returns {Promise<object>} - Response
 */
export const deleteListingVariant = async (listingId, variantId) => {
  try {
    const response = await network.networkCall(
      `v1/listings/${listingId}/variants/${variantId}`,
      'DELETE',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error deleting listing variant:', error);
    throw error;
  }
};
